import React, { useState, useEffect } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { attendanceService, authService } from '../services/api';
import Navbar from '../components/Navbar';
import './AdminDashboard.css';

const AdminDashboard = () => {
  const { user } = useAuth();
  const [stats, setStats] = useState(null);
  const [report, setReport] = useState(null);
  const [todayLog, setTodayLog] = useState(null);
  const [selectedMonth, setSelectedMonth] = useState(new Date().getMonth() + 1);
  const [selectedYear, setSelectedYear] = useState(new Date().getFullYear());
  const [loading, setLoading] = useState(false);
  const [showRegisterForm, setShowRegisterForm] = useState(false);
  const [showSettingsForm, setShowSettingsForm] = useState(false);
  const [showMemberDetail, setShowMemberDetail] = useState(null);
  const [currentTime, setCurrentTime] = useState(new Date());
  const [registerData, setRegisterData] = useState({
    name: '',
    email: '',
    password: '',
    memberType: 'PHD',
    joiningDate: '',
    role: 'student'
  });
  const [registerMessage, setRegisterMessage] = useState('');
  const [goLiveDate, setGoLiveDate] = useState(null);
  const [newGoLiveDate, setNewGoLiveDate] = useState('');
  const [settingsMessage, setSettingsMessage] = useState('');
  const [activeTab, setActiveTab] = useState('today'); // 'today', 'monthly', or 'yearly'
  const [yearlyReport, setYearlyReport] = useState(null);
  const [selectedReportYear, setSelectedReportYear] = useState(new Date().getFullYear());
  const [yearlyLoading, setYearlyLoading] = useState(false);

  const memberTypes = [
    { value: 'PHD', label: 'PhD Student' },
    { value: 'MSC', label: 'MSc Student' },
    { value: 'POSTDOC', label: 'Post-Doctoral Fellow' },
    { value: 'RA', label: 'Research Associate' },
    { value: 'INTERN', label: 'Intern' },
    { value: 'OTHER', label: 'Other' }
  ];

  // Update clock every second
  useEffect(() => {
    const timer = setInterval(() => {
      setCurrentTime(new Date());
    }, 1000);
    return () => clearInterval(timer);
  }, []);

  useEffect(() => {
    fetchStats();
    fetchTodayLog();
    fetchGoLiveDate();
  }, []);

  useEffect(() => {
    if (selectedMonth && selectedYear) {
      fetchReport();
    }
  }, [selectedMonth, selectedYear]);

  useEffect(() => {
    if (activeTab === 'yearly' && selectedReportYear) {
      fetchYearlyReport();
    }
  }, [activeTab, selectedReportYear]);

  // Auto-refresh today's log every 30 seconds
  useEffect(() => {
    const interval = setInterval(() => {
      if (activeTab === 'today') {
        fetchTodayLog();
      }
    }, 30000);
    return () => clearInterval(interval);
  }, [activeTab]);

  const fetchStats = async () => {
    try {
      const data = await attendanceService.getStats();
      setStats(data);
    } catch (error) {
      console.error('Error fetching stats:', error);
    }
  };

  const fetchTodayLog = async () => {
    try {
      const data = await attendanceService.getTodayLog();
      setTodayLog(data);
    } catch (error) {
      console.error('Error fetching today log:', error);
    }
  };

  const fetchGoLiveDate = async () => {
    try {
      const data = await attendanceService.getGoLiveDate();
      setGoLiveDate(data);
      if (data.goLiveDate) {
        setNewGoLiveDate(new Date(data.goLiveDate).toISOString().split('T')[0]);
      }
    } catch (error) {
      console.error('Error fetching go-live date:', error);
    }
  };

  const handleSetGoLiveDate = async (e) => {
    e.preventDefault();
    setSettingsMessage('');

    if (!newGoLiveDate) {
      setSettingsMessage('Please select a date');
      return;
    }

    try {
      await attendanceService.setGoLiveDate(newGoLiveDate);
      setSettingsMessage('System go-live date updated successfully!');
      fetchGoLiveDate();
      fetchReport();
      setTimeout(() => setSettingsMessage(''), 3000);
    } catch (error) {
      setSettingsMessage(error.response?.data?.message || 'Failed to update go-live date');
    }
  };

  const handleClearGoLiveDate = async () => {
    if (!window.confirm('Are you sure you want to clear the system go-live date? This will affect attendance calculations for all members.')) {
      return;
    }

    try {
      await attendanceService.clearGoLiveDate();
      setSettingsMessage('System go-live date cleared');
      setGoLiveDate(null);
      setNewGoLiveDate('');
      fetchReport();
      setTimeout(() => setSettingsMessage(''), 3000);
    } catch (error) {
      setSettingsMessage(error.response?.data?.message || 'Failed to clear go-live date');
    }
  };

  const handleDeleteMember = async (userId, memberName) => {
    if (!window.confirm(`Are you sure you want to delete ${memberName}? This will also delete all their attendance records. This action cannot be undone.`)) {
      return;
    }

    try {
      await attendanceService.deleteMember(userId);
      alert(`${memberName} has been deleted successfully.`);
      fetchStats();
      fetchReport();
      fetchTodayLog();
    } catch (error) {
      alert(error.response?.data?.message || 'Failed to delete member');
    }
  };

  const fetchReport = async () => {
    setLoading(true);
    try {
      const data = await attendanceService.getMonthlyReport(selectedMonth, selectedYear);
      setReport(data);
    } catch (error) {
      console.error('Error fetching report:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchYearlyReport = async () => {
    setYearlyLoading(true);
    try {
      const data = await attendanceService.getYearlyReport(selectedReportYear);
      setYearlyReport(data);
    } catch (error) {
      console.error('Error fetching yearly report:', error);
    } finally {
      setYearlyLoading(false);
    }
  };

  const handleRegisterSubmit = async (e) => {
    e.preventDefault();
    setRegisterMessage('');

    try {
      await authService.register(registerData);
      setRegisterMessage('User registered successfully!');
      setRegisterData({
        name: '',
        email: '',
        password: '',
        memberType: 'PHD',
        joiningDate: '',
        role: 'student'
      });
      fetchStats();
      fetchTodayLog();
      setTimeout(() => {
        setShowRegisterForm(false);
        setRegisterMessage('');
      }, 2000);
    } catch (error) {
      setRegisterMessage(error.response?.data?.message || 'Registration failed');
    }
  };

  const formatTime = (dateTime) => {
    if (!dateTime) return '-';
    return new Date(dateTime).toLocaleTimeString('en-US', {
      hour: '2-digit',
      minute: '2-digit',
      second: '2-digit',
      hour12: true
    });
  };

  const formatDate = (date) => {
    return new Date(date).toLocaleDateString('en-US', {
      weekday: 'short',
      month: 'short',
      day: 'numeric',
      year: 'numeric'
    });
  };

  const getStatusText = (status) => {
    switch (status) {
      case 'present': return 'Present';
      case 'absent': return 'Absent';
      case 'not_marked': return 'Not Marked';
      default: return status;
    }
  };

  // Export Today's Log as CSV
  const exportTodayLogCSV = () => {
    if (!todayLog || !todayLog.attendanceLog.length) return;

    const headers = ['#', 'Name', 'Lab ID', 'Member Type', 'Status', 'Check-in Time', 'Remarks'];
    const rows = todayLog.attendanceLog.map((member, index) => [
      index + 1,
      member.name,
      member.labId,
      member.memberType,
      getStatusText(member.status),
      member.checkInTime ? formatTime(member.checkInTime) : '-',
      member.remarks || '-'
    ]);

    const csvContent = [
      ['RNA Biology Lab - Daily Attendance Log'],
      [`Date: ${todayLog.dateFormatted}`],
      [`Generated: ${new Date().toLocaleString()}`],
      [],
      [`Total Members: ${todayLog.summary.total}`],
      [`Present: ${todayLog.summary.present} | Absent: ${todayLog.summary.absent} | Not Marked: ${todayLog.summary.notMarked}`],
      [],
      headers,
      ...rows
    ].map(row => row.join(',')).join('\n');

    const blob = new Blob([csvContent], { type: 'text/csv' });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `daily_attendance_${new Date().toISOString().split('T')[0]}.csv`;
    a.click();
    window.URL.revokeObjectURL(url);
  };

  // Export Monthly Summary CSV
  const exportToCSV = () => {
    if (!report || !report.report) return;

    const monthName = new Date(selectedYear, selectedMonth - 1).toLocaleString('default', { month: 'long' });
    const headers = ['Name', 'Lab ID', 'Member Type', 'Email', 'Working Days', 'Present', 'Absent', 'Attendance %'];
    const rows = report.report.map(student => [
      student.name,
      student.labId,
      student.memberType || 'N/A',
      student.email,
      student.workingDays || student.totalDays,
      student.totalPresent,
      student.totalAbsent,
      `${student.attendancePercentage}%`
    ]);

    const totalPresent = report.report.reduce((sum, s) => sum + s.totalPresent, 0);
    const avgAttendance = report.report.length > 0
      ? (report.report.reduce((sum, s) => sum + s.attendancePercentage, 0) / report.report.length).toFixed(1)
      : 0;

    const csvContent = [
      ['RNA Biology Lab - Attendance Report'],
      [`Period: ${monthName} ${selectedYear}`],
      [`Generated: ${new Date().toLocaleString()}`],
      [`Total Students: ${report.activeStudents || report.totalStudents}`],
      [`Working Days: ${report.totalWorkingDays || report.totalDays}`],
      report.isCurrentMonth ? ['Note: Current month - data up to today only'] : [],
      [],
      headers,
      ...rows,
      [],
      ['Summary'],
      ['Total Attendance Records', totalPresent],
      ['Average Attendance Rate', `${avgAttendance}%`]
    ].map(row => row.join(',')).join('\n');

    const blob = new Blob([csvContent], { type: 'text/csv' });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `lab_attendance_${monthName}_${selectedYear}.csv`;
    a.click();
    window.URL.revokeObjectURL(url);
  };

  // Export Detailed CSV with Check-in Times
  const exportDetailedReport = () => {
    if (!report || !report.report) return;

    const monthName = new Date(selectedYear, selectedMonth - 1).toLocaleString('default', { month: 'long' });

    let csvContent = [
      ['RNA Biology Lab - Detailed Attendance Report with Check-in Times'],
      [`Period: ${monthName} ${selectedYear}`],
      [`Generated: ${new Date().toLocaleString()}`],
      [],
    ].map(row => row.join(',')).join('\n');

    // For each student, add their detailed attendance
    report.report.forEach(student => {
      csvContent += '\n\n';
      csvContent += `"${student.name}","${student.labId}","${student.memberType || 'N/A'}"\n`;
      csvContent += `"Email:","${student.email}"\n`;
      csvContent += `"Working Days:","${student.workingDays}","Present:","${student.totalPresent}","Absent:","${student.totalAbsent}","Attendance:","${student.attendancePercentage}%"\n`;

      if (student.attendanceDetails && student.attendanceDetails.length > 0) {
        csvContent += '\n"Date","Day","Status","Check-in Time","Remarks"\n';
        student.attendanceDetails.forEach(record => {
          const recordDate = new Date(record.date);
          csvContent += `"${recordDate.toLocaleDateString()}","${recordDate.toLocaleDateString('en-US', { weekday: 'long' })}","${getStatusText(record.status)}","${record.checkInTime ? formatTime(record.checkInTime) : '-'}","${record.remarks || '-'}"\n`;
        });
      } else {
        csvContent += '"No detailed records available for this period"\n';
      }
    });

    const blob = new Blob([csvContent], { type: 'text/csv' });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `detailed_attendance_${monthName}_${selectedYear}.csv`;
    a.click();
    window.URL.revokeObjectURL(url);
  };

  // Export PDF with Check-in Times
  const exportToPDF = () => {
    if (!report || !report.report) return;

    const monthName = new Date(selectedYear, selectedMonth - 1).toLocaleString('default', { month: 'long' });
    const totalPresent = report.report.reduce((sum, s) => sum + s.totalPresent, 0);
    const avgAttendance = report.report.length > 0
      ? (report.report.reduce((sum, s) => sum + s.attendancePercentage, 0) / report.report.length).toFixed(1)
      : 0;
    const workingDays = report.totalWorkingDays || report.totalDays;

    const printContent = `
      <!DOCTYPE html>
      <html>
      <head>
        <title>Lab Attendance Report - ${monthName} ${selectedYear}</title>
        <style>
          body { font-family: Arial, sans-serif; padding: 30px; color: #333; }
          .header { text-align: center; margin-bottom: 30px; border-bottom: 3px solid #1a1a2e; padding-bottom: 20px; }
          .header h1 { color: #1a1a2e; margin-bottom: 5px; font-size: 24px; }
          .header h2 { color: #666; font-weight: normal; font-size: 18px; margin-top: 5px; }
          .current-month-note { background: #fff3cd; color: #856404; padding: 8px 15px; border-radius: 4px; margin-top: 10px; font-size: 12px; }
          .meta { display: flex; justify-content: space-between; margin-bottom: 20px; background: #f5f5f5; padding: 15px; border-radius: 8px; flex-wrap: wrap; gap: 10px; }
          .meta-item { text-align: center; flex: 1; min-width: 120px; }
          .meta-item .value { font-size: 20px; font-weight: bold; color: #1a1a2e; }
          .meta-item .label { color: #666; font-size: 11px; }
          table { width: 100%; border-collapse: collapse; margin: 20px 0; }
          th { background: #1a1a2e; color: white; padding: 10px; text-align: left; font-size: 10px; }
          td { padding: 8px 10px; border-bottom: 1px solid #ddd; font-size: 10px; }
          tr:nth-child(even) { background: #f9f9f9; }
          .good { color: #28a745; font-weight: bold; }
          .warning { color: #ffc107; font-weight: bold; }
          .poor { color: #dc3545; font-weight: bold; }
          .summary { margin-top: 30px; display: grid; grid-template-columns: repeat(3, 1fr); gap: 15px; }
          .summary-card { background: #f5f5f5; padding: 20px; border-radius: 8px; text-align: center; }
          .summary-card .value { font-size: 24px; font-weight: bold; color: #1a1a2e; }
          .summary-card .label { color: #666; font-size: 11px; margin-top: 5px; }
          .member-detail { page-break-inside: avoid; margin-top: 30px; border: 1px solid #ddd; border-radius: 8px; padding: 15px; }
          .member-header { display: flex; justify-content: space-between; align-items: center; border-bottom: 2px solid #1a1a2e; padding-bottom: 10px; margin-bottom: 15px; }
          .member-name { font-size: 16px; font-weight: bold; color: #1a1a2e; }
          .member-stats { display: flex; gap: 20px; font-size: 11px; }
          .detail-table { width: 100%; }
          .detail-table th { background: #5a7a8a; font-size: 9px; padding: 6px; }
          .detail-table td { font-size: 9px; padding: 6px; }
          .footer { margin-top: 40px; text-align: center; color: #888; font-size: 10px; border-top: 1px solid #ddd; padding-top: 20px; }
          @media print {
            body { padding: 15px; }
            .no-print { display: none; }
            .member-detail { page-break-inside: avoid; }
          }
        </style>
      </head>
      <body>
        <div class="header">
          <h1>RNA Biology Lab</h1>
          <h2>Monthly Attendance Report with Check-in Times</h2>
          ${report.isCurrentMonth ? '<div class="current-month-note">Note: Current month - Data calculated up to today only</div>' : ''}
        </div>

        <div class="meta">
          <div class="meta-item">
            <div class="value">${monthName} ${selectedYear}</div>
            <div class="label">Report Period</div>
          </div>
          <div class="meta-item">
            <div class="value">${report.activeStudents || report.totalStudents}</div>
            <div class="label">Active Lab Members</div>
          </div>
          <div class="meta-item">
            <div class="value">${workingDays}</div>
            <div class="label">Working Days</div>
          </div>
          <div class="meta-item">
            <div class="value">${new Date().toLocaleDateString()}</div>
            <div class="label">Generated On</div>
          </div>
        </div>

        <h3 style="color: #1a1a2e; border-bottom: 2px solid #e0e0e0; padding-bottom: 10px;">Summary Overview</h3>
        <table>
          <thead>
            <tr>
              <th>#</th>
              <th>Name</th>
              <th>Lab ID</th>
              <th>Type</th>
              <th>Working Days</th>
              <th>Present</th>
              <th>Absent</th>
              <th>Attendance %</th>
            </tr>
          </thead>
          <tbody>
            ${report.report.map((student, index) => `
              <tr>
                <td>${index + 1}</td>
                <td>${student.name}</td>
                <td>${student.labId}</td>
                <td>${student.memberType || 'N/A'}</td>
                <td>${student.workingDays || '-'}</td>
                <td>${student.totalPresent}</td>
                <td>${student.totalAbsent}</td>
                <td class="${student.attendancePercentage >= 75 ? 'good' : student.attendancePercentage >= 50 ? 'warning' : 'poor'}">
                  ${student.attendancePercentage.toFixed(1)}%
                </td>
              </tr>
            `).join('')}
          </tbody>
        </table>

        <div class="summary">
          <div class="summary-card">
            <div class="value">${totalPresent}</div>
            <div class="label">Total Attendance Records</div>
          </div>
          <div class="summary-card">
            <div class="value">${avgAttendance}%</div>
            <div class="label">Average Attendance Rate</div>
          </div>
          <div class="summary-card">
            <div class="value">${report.report.filter(s => s.attendancePercentage >= 75).length}</div>
            <div class="label">Members with Good Attendance (≥75%)</div>
          </div>
        </div>

        <h3 style="color: #1a1a2e; border-bottom: 2px solid #e0e0e0; padding-bottom: 10px; margin-top: 40px;">Detailed Attendance with Check-in Times</h3>

        ${report.report.map(student => `
          <div class="member-detail">
            <div class="member-header">
              <div class="member-name">${student.name} (${student.labId})</div>
              <div class="member-stats">
                <span>Present: ${student.totalPresent}</span>
                <span>Absent: ${student.totalAbsent}</span>
                <span>Attendance: ${student.attendancePercentage.toFixed(1)}%</span>
              </div>
            </div>
            ${student.attendanceDetails && student.attendanceDetails.length > 0 ? `
              <table class="detail-table">
                <thead>
                  <tr>
                    <th>Date</th>
                    <th>Day</th>
                    <th>Status</th>
                    <th>Check-in Time</th>
                    <th>Remarks</th>
                  </tr>
                </thead>
                <tbody>
                  ${student.attendanceDetails.map(record => {
                    const recordDate = new Date(record.date);
                    return `
                      <tr>
                        <td>${recordDate.toLocaleDateString()}</td>
                        <td>${recordDate.toLocaleDateString('en-US', { weekday: 'short' })}</td>
                        <td class="${record.status === 'present' ? 'good' : 'poor'}">${record.status === 'present' ? 'Present' : 'Absent'}</td>
                        <td><strong>${record.checkInTime ? new Date(record.checkInTime).toLocaleTimeString('en-US', { hour: '2-digit', minute: '2-digit', hour12: true }) : '-'}</strong></td>
                        <td>${record.remarks || '-'}</td>
                      </tr>
                    `;
                  }).join('')}
                </tbody>
              </table>
            ` : '<p style="color: #888; font-size: 11px;">No detailed records for this period</p>'}
          </div>
        `).join('')}

        <div class="footer">
          <p>This is an automatically generated report from RNA Biology Lab Attendance System</p>
          <p>Attendance calculated based on working days (Monday-Friday) only</p>
          <p>Check-in times are recorded when members mark their presence</p>
        </div>
      </body>
      </html>
    `;

    const printWindow = window.open('', '_blank');
    printWindow.document.write(printContent);
    printWindow.document.close();
    printWindow.focus();
    setTimeout(() => {
      printWindow.print();
    }, 250);
  };

  // Export Yearly Report as CSV
  const exportYearlyCSV = () => {
    if (!yearlyReport || !yearlyReport.report) return;

    const headers = ['Name', 'Lab ID', 'Type', 'Working Days', 'Present', 'Absent', 'Not Marked', 'Attendance %'];
    const rows = yearlyReport.report.map(student => [
      student.name,
      student.labId,
      student.memberType || 'N/A',
      student.workingDays,
      student.totalPresent,
      student.totalAbsent,
      student.notMarked,
      `${student.attendancePercentage}%`
    ]);

    const avgAttendance = yearlyReport.report.length > 0
      ? (yearlyReport.report.reduce((sum, s) => sum + s.attendancePercentage, 0) / yearlyReport.report.length).toFixed(1)
      : 0;

    const csvContent = [
      ['RNA Biology Lab - Yearly Attendance Report'],
      [`Year: ${yearlyReport.year}`],
      [`Generated: ${new Date().toLocaleString()}`],
      [`Total Working Days: ${yearlyReport.totalWorkingDays}`],
      [`Active Members: ${yearlyReport.activeStudents}`],
      yearlyReport.isCurrentYear ? ['Note: Current year - data up to today only'] : [],
      [],
      headers,
      ...rows,
      [],
      ['Summary'],
      ['Average Attendance Rate', `${avgAttendance}%`]
    ].map(row => row.join(',')).join('\n');

    const blob = new Blob([csvContent], { type: 'text/csv' });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `yearly_attendance_${yearlyReport.year}.csv`;
    a.click();
    window.URL.revokeObjectURL(url);
  };

  // Export Yearly Detailed CSV with Monthly Breakdown
  const exportYearlyDetailedCSV = () => {
    if (!yearlyReport || !yearlyReport.report) return;

    let csvContent = [
      ['RNA Biology Lab - Yearly Detailed Attendance Report'],
      [`Year: ${yearlyReport.year}`],
      [`Generated: ${new Date().toLocaleString()}`],
      [`Total Working Days: ${yearlyReport.totalWorkingDays}`],
      [],
    ].map(row => row.join(',')).join('\n');

    yearlyReport.report.forEach(student => {
      csvContent += '\n\n';
      csvContent += `"${student.name}","${student.labId}","${student.memberType || 'N/A'}"\n`;
      csvContent += `"Email:","${student.email}"\n`;
      csvContent += `"Yearly Summary - Working Days:","${student.workingDays}","Present:","${student.totalPresent}","Absent:","${student.totalAbsent}","Attendance:","${student.attendancePercentage}%"\n`;

      if (student.monthlyBreakdown && student.monthlyBreakdown.length > 0) {
        csvContent += '\n"Month","Working Days","Present","Absent","Not Marked","Attendance %"\n';
        student.monthlyBreakdown.forEach(month => {
          csvContent += `"${month.monthName}","${month.workingDays}","${month.present}","${month.absent}","${month.notMarked}","${month.percentage}%"\n`;
        });
      }
    });

    const blob = new Blob([csvContent], { type: 'text/csv' });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `yearly_detailed_attendance_${yearlyReport.year}.csv`;
    a.click();
    window.URL.revokeObjectURL(url);
  };

  // Export Yearly PDF
  const exportYearlyPDF = () => {
    if (!yearlyReport || !yearlyReport.report) return;

    const avgAttendance = yearlyReport.report.length > 0
      ? (yearlyReport.report.reduce((sum, s) => sum + s.attendancePercentage, 0) / yearlyReport.report.length).toFixed(1)
      : 0;

    const printContent = `
      <!DOCTYPE html>
      <html>
      <head>
        <title>Yearly Attendance Report - ${yearlyReport.year}</title>
        <style>
          body { font-family: Arial, sans-serif; padding: 30px; color: #333; }
          .header { text-align: center; margin-bottom: 30px; border-bottom: 3px solid #1a1a2e; padding-bottom: 20px; }
          .header h1 { color: #1a1a2e; margin-bottom: 5px; font-size: 24px; }
          .header h2 { color: #666; font-weight: normal; font-size: 18px; margin-top: 5px; }
          .meta { display: flex; justify-content: space-between; margin-bottom: 20px; background: #f5f5f5; padding: 15px; border-radius: 8px; flex-wrap: wrap; gap: 10px; }
          .meta-item { text-align: center; flex: 1; min-width: 100px; }
          .meta-item .value { font-size: 18px; font-weight: bold; color: #1a1a2e; }
          .meta-item .label { color: #666; font-size: 10px; }
          table { width: 100%; border-collapse: collapse; margin: 20px 0; }
          th { background: #1a1a2e; color: white; padding: 10px; text-align: left; font-size: 10px; }
          td { padding: 8px 10px; border-bottom: 1px solid #ddd; font-size: 10px; }
          tr:nth-child(even) { background: #f9f9f9; }
          .good { color: #28a745; font-weight: bold; }
          .warning { color: #ffc107; font-weight: bold; }
          .poor { color: #dc3545; font-weight: bold; }
          .member-section { page-break-inside: avoid; margin-top: 25px; border: 1px solid #ddd; border-radius: 8px; padding: 15px; }
          .member-header { font-size: 14px; font-weight: bold; color: #1a1a2e; border-bottom: 2px solid #1a1a2e; padding-bottom: 8px; margin-bottom: 10px; }
          .monthly-table { width: 100%; }
          .monthly-table th { background: #5a7a8a; font-size: 9px; padding: 6px; }
          .monthly-table td { font-size: 9px; padding: 6px; text-align: center; }
          .summary { margin-top: 30px; display: grid; grid-template-columns: repeat(3, 1fr); gap: 15px; }
          .summary-card { background: #f5f5f5; padding: 20px; border-radius: 8px; text-align: center; }
          .summary-card .value { font-size: 24px; font-weight: bold; color: #1a1a2e; }
          .summary-card .label { color: #666; font-size: 11px; margin-top: 5px; }
          .footer { margin-top: 40px; text-align: center; color: #888; font-size: 10px; border-top: 1px solid #ddd; padding-top: 20px; }
          @media print { .member-section { page-break-inside: avoid; } }
        </style>
      </head>
      <body>
        <div class="header">
          <h1>RNA Biology Lab</h1>
          <h2>Yearly Attendance Report - ${yearlyReport.year}</h2>
          ${yearlyReport.isCurrentYear ? '<div style="background:#fff3cd;color:#856404;padding:8px;border-radius:4px;margin-top:10px;font-size:12px;">Current year - Data up to today</div>' : ''}
        </div>

        <div class="meta">
          <div class="meta-item">
            <div class="value">${yearlyReport.year}</div>
            <div class="label">Report Year</div>
          </div>
          <div class="meta-item">
            <div class="value">${yearlyReport.activeStudents}</div>
            <div class="label">Active Members</div>
          </div>
          <div class="meta-item">
            <div class="value">${yearlyReport.totalWorkingDays}</div>
            <div class="label">Working Days</div>
          </div>
          <div class="meta-item">
            <div class="value">${avgAttendance}%</div>
            <div class="label">Avg Attendance</div>
          </div>
        </div>

        <h3 style="color:#1a1a2e;border-bottom:2px solid #e0e0e0;padding-bottom:10px;">Yearly Summary</h3>
        <table>
          <thead>
            <tr>
              <th>#</th>
              <th>Name</th>
              <th>Lab ID</th>
              <th>Type</th>
              <th>Working Days</th>
              <th>Present</th>
              <th>Absent</th>
              <th>Not Marked</th>
              <th>Attendance %</th>
            </tr>
          </thead>
          <tbody>
            ${yearlyReport.report.map((student, index) => `
              <tr>
                <td>${index + 1}</td>
                <td>${student.name}</td>
                <td>${student.labId}</td>
                <td>${student.memberType || 'N/A'}</td>
                <td>${student.workingDays}</td>
                <td>${student.totalPresent}</td>
                <td>${student.totalAbsent}</td>
                <td>${student.notMarked}</td>
                <td class="${student.attendancePercentage >= 75 ? 'good' : student.attendancePercentage >= 50 ? 'warning' : 'poor'}">
                  ${student.attendancePercentage.toFixed(1)}%
                </td>
              </tr>
            `).join('')}
          </tbody>
        </table>

        <h3 style="color:#1a1a2e;border-bottom:2px solid #e0e0e0;padding-bottom:10px;margin-top:40px;">Monthly Breakdown by Member</h3>

        ${yearlyReport.report.map(student => `
          <div class="member-section">
            <div class="member-header">${student.name} (${student.labId}) - ${student.attendancePercentage.toFixed(1)}% Overall</div>
            ${student.monthlyBreakdown && student.monthlyBreakdown.length > 0 ? `
              <table class="monthly-table">
                <thead>
                  <tr>
                    <th>Month</th>
                    <th>Working Days</th>
                    <th>Present</th>
                    <th>Absent</th>
                    <th>Not Marked</th>
                    <th>Attendance %</th>
                  </tr>
                </thead>
                <tbody>
                  ${student.monthlyBreakdown.map(month => `
                    <tr>
                      <td><strong>${month.monthName}</strong></td>
                      <td>${month.workingDays}</td>
                      <td>${month.present}</td>
                      <td>${month.absent}</td>
                      <td>${month.notMarked}</td>
                      <td class="${parseFloat(month.percentage) >= 75 ? 'good' : parseFloat(month.percentage) >= 50 ? 'warning' : 'poor'}">${month.percentage}%</td>
                    </tr>
                  `).join('')}
                </tbody>
              </table>
            ` : '<p style="color:#888;font-size:11px;">No data for this year</p>'}
          </div>
        `).join('')}

        <div class="footer">
          <p>RNA Biology Lab Attendance System - Yearly Report</p>
          <p>Generated on ${new Date().toLocaleString()}</p>
        </div>
      </body>
      </html>
    `;

    const printWindow = window.open('', '_blank');
    printWindow.document.write(printContent);
    printWindow.document.close();
    printWindow.focus();
    setTimeout(() => {
      printWindow.print();
    }, 250);
  };

  return (
    <div className="admin-dashboard-page">
      <Navbar />
      <div className="admin-dashboard-container">
        <div className="admin-actions-bar">
          <button onClick={() => setShowRegisterForm(!showRegisterForm)} className="add-user-button">
            {showRegisterForm ? '✕ Close Form' : '+ Add New Member'}
          </button>
          <button onClick={() => setShowSettingsForm(!showSettingsForm)} className="settings-button">
            {showSettingsForm ? '✕ Close Settings' : '⚙ System Settings'}
          </button>
        </div>

        {showRegisterForm && (
          <div className="register-form-card">
            <h2>Register New Lab Member</h2>
            <form onSubmit={handleRegisterSubmit} className="register-form">
              <div className="form-row">
                <div className="form-group">
                  <label>Full Name</label>
                  <input
                    type="text"
                    value={registerData.name}
                    onChange={(e) => setRegisterData({ ...registerData, name: e.target.value })}
                    required
                    placeholder="Enter full name"
                  />
                </div>
                <div className="form-group">
                  <label>Email Address</label>
                  <input
                    type="email"
                    value={registerData.email}
                    onChange={(e) => setRegisterData({ ...registerData, email: e.target.value })}
                    required
                    placeholder="Enter email"
                  />
                </div>
              </div>
              <div className="form-row">
                <div className="form-group">
                  <label>Password</label>
                  <input
                    type="password"
                    value={registerData.password}
                    onChange={(e) => setRegisterData({ ...registerData, password: e.target.value })}
                    required
                    minLength={6}
                    placeholder="Min 6 characters"
                  />
                </div>
                <div className="form-group">
                  <label>Member Type</label>
                  <select
                    value={registerData.memberType}
                    onChange={(e) => setRegisterData({ ...registerData, memberType: e.target.value })}
                    required
                  >
                    {memberTypes.map(type => (
                      <option key={type.value} value={type.value}>
                        {type.label}
                      </option>
                    ))}
                  </select>
                </div>
              </div>
              <div className="form-row">
                <div className="form-group">
                  <label>Lab Joining Date</label>
                  <input
                    type="date"
                    value={registerData.joiningDate}
                    onChange={(e) => setRegisterData({ ...registerData, joiningDate: e.target.value })}
                    required
                    max={new Date().toISOString().split('T')[0]}
                  />
                </div>
                <div className="form-group">
                  <label>Role</label>
                  <select
                    value={registerData.role}
                    onChange={(e) => setRegisterData({ ...registerData, role: e.target.value })}
                  >
                    <option value="student">Lab Member</option>
                    <option value="admin">Admin / PI</option>
                  </select>
                </div>
              </div>
              <small className="field-hint">Lab ID will be auto-generated (e.g., PHD2025001)</small>
              <button type="submit" className="submit-button">Register Member</button>
              {registerMessage && (
                <div className={registerMessage.includes('success') ? 'success-message' : 'error-message'}>
                  {registerMessage}
                </div>
              )}
            </form>
          </div>
        )}

        {showSettingsForm && (
          <div className="settings-form-card">
            <h2>System Settings</h2>
            <div className="settings-section">
              <h3>System Go-Live Date</h3>
              <p className="settings-description">
                Set the date when this attendance system went live.
                Days before this date will not be counted as absences for any member.
                This is useful when deploying the system mid-month.
              </p>

              <div className="current-setting">
                <span className="setting-label">Current Go-Live Date:</span>
                <span className={`setting-value ${goLiveDate?.isSet ? 'set' : 'not-set'}`}>
                  {goLiveDate?.isSet
                    ? new Date(goLiveDate.goLiveDate).toLocaleDateString('en-US', {
                        weekday: 'long',
                        year: 'numeric',
                        month: 'long',
                        day: 'numeric'
                      })
                    : 'Not configured (using member joining dates only)'}
                </span>
              </div>

              <form onSubmit={handleSetGoLiveDate} className="settings-form">
                <div className="form-group">
                  <label>Set Go-Live Date</label>
                  <input
                    type="date"
                    value={newGoLiveDate}
                    onChange={(e) => setNewGoLiveDate(e.target.value)}
                    max={new Date().toISOString().split('T')[0]}
                  />
                </div>
                <div className="settings-buttons">
                  <button type="submit" className="save-button">
                    Save Go-Live Date
                  </button>
                  {goLiveDate?.isSet && (
                    <button type="button" onClick={handleClearGoLiveDate} className="clear-button">
                      Clear Go-Live Date
                    </button>
                  )}
                </div>
              </form>

              {settingsMessage && (
                <div className={settingsMessage.includes('success') || settingsMessage.includes('cleared') ? 'success-message' : 'error-message'}>
                  {settingsMessage}
                </div>
              )}

              <div className="settings-info">
                <strong>How it works:</strong>
                <ul>
                  <li>Attendance calculations will start from MAX(go-live date, member joining date)</li>
                  <li>Days before the go-live date are never counted as absences</li>
                  <li>This applies to all past and future reports</li>
                </ul>
              </div>
            </div>
          </div>
        )}

        <div className="admin-dashboard-content">
          {stats && (
            <div className="stats-overview">
              <div className="stat-card">
                <div className="stat-icon">👥</div>
                <div className="stat-info">
                  <div className="stat-number">{stats.totalStudents}</div>
                  <div className="stat-label">Total Lab Members</div>
                </div>
              </div>
              <div className="stat-card present">
                <div className="stat-icon">✓</div>
                <div className="stat-info">
                  <div className="stat-number">{stats.presentToday}</div>
                  <div className="stat-label">Present Today</div>
                </div>
              </div>
              <div className="stat-card absent">
                <div className="stat-icon">✗</div>
                <div className="stat-info">
                  <div className="stat-number">{stats.absentToday}</div>
                  <div className="stat-label">Absent Today</div>
                </div>
              </div>
              <div className="stat-card not-marked">
                <div className="stat-icon">!</div>
                <div className="stat-info">
                  <div className="stat-number">{stats.notMarkedToday || 0}</div>
                  <div className="stat-label">Not Marked</div>
                </div>
              </div>
            </div>
          )}

          {/* Tab Navigation */}
          <div className="tab-navigation">
            <button
              className={`tab-btn ${activeTab === 'today' ? 'active' : ''}`}
              onClick={() => setActiveTab('today')}
            >
              Today's Check-ins
            </button>
            <button
              className={`tab-btn ${activeTab === 'monthly' ? 'active' : ''}`}
              onClick={() => setActiveTab('monthly')}
            >
              Monthly Report
            </button>
            <button
              className={`tab-btn ${activeTab === 'yearly' ? 'active' : ''}`}
              onClick={() => setActiveTab('yearly')}
            >
              Yearly Report
            </button>
          </div>

          {/* Today's Check-in Log */}
          {activeTab === 'today' && (
            <div className="report-section today-log-section">
              <div className="report-header">
                <div>
                  <h2>Today's Attendance Log</h2>
                  <p className="date-subtitle">{todayLog?.dateFormatted || new Date().toLocaleDateString('en-US', { weekday: 'long', year: 'numeric', month: 'long', day: 'numeric' })}</p>
                </div>
                <div className="report-controls">
                  <button onClick={fetchTodayLog} className="refresh-btn" title="Refresh">🔄</button>
                  <button onClick={exportTodayLogCSV} className="export-btn csv" disabled={!todayLog?.attendanceLog?.length}>
                    📊 Export Today's Log
                  </button>
                </div>
              </div>

              {todayLog?.isWeekend ? (
                <div className="weekend-notice">
                  <span className="weekend-icon">📅</span>
                  <p>Today is a weekend. No attendance tracking on weekends.</p>
                </div>
              ) : todayLog?.attendanceLog?.length > 0 ? (
                <div className="report-table-container">
                  <table className="report-table today-log-table">
                    <thead>
                      <tr>
                        <th>#</th>
                        <th>Name</th>
                        <th>Lab ID</th>
                        <th>Type</th>
                        <th>Status</th>
                        <th>Check-in Time</th>
                        <th>Remarks</th>
                      </tr>
                    </thead>
                    <tbody>
                      {todayLog.attendanceLog.map((member, index) => (
                        <tr key={member.userId} className={`status-row ${member.status}`}>
                          <td>{index + 1}</td>
                          <td className="name-cell">{member.name}</td>
                          <td>{member.labId}</td>
                          <td>{member.memberType}</td>
                          <td>
                            <span className={`status-badge ${member.status}`}>
                              {getStatusText(member.status)}
                            </span>
                          </td>
                          <td className="checkin-time-cell">
                            {member.checkInTime ? (
                              <strong>{formatTime(member.checkInTime)}</strong>
                            ) : (
                              <span className="no-time">-</span>
                            )}
                          </td>
                          <td className="remarks-cell">{member.remarks || '-'}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              ) : (
                <div className="no-data">Loading today's attendance log...</div>
              )}
            </div>
          )}

          {/* Monthly Report */}
          {activeTab === 'monthly' && (
            <div className="report-section">
              <div className="report-header">
                <h2>Monthly Attendance Report</h2>
                <div className="report-controls">
                  <select
                    value={selectedMonth}
                    onChange={(e) => setSelectedMonth(parseInt(e.target.value))}
                  >
                    {Array.from({ length: 12 }, (_, i) => (
                      <option key={i + 1} value={i + 1}>
                        {new Date(2000, i).toLocaleString('default', { month: 'long' })}
                      </option>
                    ))}
                  </select>
                  <select
                    value={selectedYear}
                    onChange={(e) => setSelectedYear(parseInt(e.target.value))}
                  >
                    {Array.from({ length: 5 }, (_, i) => (
                      <option key={i} value={new Date().getFullYear() - 2 + i}>
                        {new Date().getFullYear() - 2 + i}
                      </option>
                    ))}
                  </select>
                  <button onClick={fetchReport} className="refresh-btn" title="Refresh Report">🔄</button>
                </div>
              </div>

              <div className="export-buttons">
                <button onClick={exportToCSV} className="export-btn csv" title="Download Summary CSV">
                  📊 Summary CSV
                </button>
                <button onClick={exportDetailedReport} className="export-btn detailed" title="Download Detailed CSV with Check-in Times">
                  📋 Detailed CSV (with times)
                </button>
                <button onClick={exportToPDF} className="export-btn pdf" title="Download PDF Report with Check-in Times">
                  📄 Full PDF Report
                </button>
              </div>

              {loading ? (
                <div className="loading-state">
                  <div className="loading-spinner-large"></div>
                  <p>Loading report...</p>
                </div>
              ) : report ? (
                <div className="report-content">
                  <div className="report-summary">
                    <div className="summary-item">
                      <span className="summary-label">Period:</span>
                      <span className="summary-value">{report.monthName} {report.year}</span>
                    </div>
                    <div className="summary-item">
                      <span className="summary-label">Working Days:</span>
                      <span className="summary-value">{report.totalWorkingDays || report.totalDays}</span>
                    </div>
                    <div className="summary-item">
                      <span className="summary-label">Total Members:</span>
                      <span className="summary-value">{report.activeStudents || report.totalStudents}</span>
                    </div>
                    {report.isCurrentMonth && (
                      <div className="summary-item">
                        <span className="summary-label current-month-badge">Current Month (up to today)</span>
                      </div>
                    )}
                    {report.systemGoLiveDate && (
                      <div className="summary-item">
                        <span className="summary-label go-live-badge">
                          System active from: {new Date(report.systemGoLiveDate).toLocaleDateString()}
                        </span>
                      </div>
                    )}
                  </div>

                  <div className="report-table-container">
                    <table className="report-table">
                      <thead>
                        <tr>
                          <th>#</th>
                          <th>Name</th>
                          <th>Lab ID</th>
                          <th>Present</th>
                          <th>Absent</th>
                          <th>Not Marked</th>
                          <th>Attendance %</th>
                          <th>Details</th>
                          <th>Actions</th>
                        </tr>
                      </thead>
                      <tbody>
                        {report.report.map((student, index) => (
                          <tr key={index}>
                            <td>{index + 1}</td>
                            <td className="name-cell">{student.name}</td>
                            <td>{student.labId}</td>
                            <td className="present-count">{student.totalPresent}</td>
                            <td className="absent-count">{student.totalAbsent}</td>
                            <td className="not-marked-count">{student.notMarked || 0}</td>
                            <td>
                              <div className="percentage-cell">
                                <span className={`percentage-badge ${
                                  student.attendancePercentage >= 75 ? 'good' :
                                  student.attendancePercentage >= 50 ? 'warning' : 'poor'
                                }`}>
                                  {student.attendancePercentage.toFixed(1)}%
                                </span>
                              </div>
                            </td>
                            <td>
                              <button
                                className="view-details-btn"
                                onClick={() => setShowMemberDetail(student)}
                                title="View detailed attendance with check-in times"
                              >
                                👁 View
                              </button>
                            </td>
                            <td>
                              <button
                                className="delete-btn"
                                onClick={() => handleDeleteMember(student.id, student.name)}
                                title="Delete member"
                              >
                                🗑️
                              </button>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>

                  {report.report.length > 0 && (
                    <div className="report-footer-stats">
                      <div className="footer-stat">
                        <span className="footer-stat-value">
                          {(report.report.reduce((sum, s) => sum + s.attendancePercentage, 0) / report.report.length).toFixed(1)}%
                        </span>
                        <span className="footer-stat-label">Average Attendance</span>
                      </div>
                      <div className="footer-stat">
                        <span className="footer-stat-value">
                          {report.report.filter(s => s.attendancePercentage >= 75).length}
                        </span>
                        <span className="footer-stat-label">Good Attendance (≥75%)</span>
                      </div>
                      <div className="footer-stat">
                        <span className="footer-stat-value">
                          {report.report.filter(s => s.attendancePercentage < 50).length}
                        </span>
                        <span className="footer-stat-label">Needs Attention (&lt;50%)</span>
                      </div>
                    </div>
                  )}
                </div>
              ) : (
                <div className="no-data">No report data available</div>
              )}
            </div>
          )}

          {/* Yearly Report */}
          {activeTab === 'yearly' && (
            <div className="report-section">
              <div className="report-header">
                <h2>Yearly Attendance Report</h2>
                <div className="report-controls">
                  <select
                    value={selectedReportYear}
                    onChange={(e) => setSelectedReportYear(parseInt(e.target.value))}
                  >
                    {Array.from({ length: 5 }, (_, i) => (
                      <option key={i} value={new Date().getFullYear() - i}>
                        {new Date().getFullYear() - i}
                      </option>
                    ))}
                  </select>
                  <button onClick={fetchYearlyReport} className="refresh-btn" title="Refresh Report">🔄</button>
                </div>
              </div>

              <div className="export-buttons">
                <button onClick={exportYearlyCSV} className="export-btn csv" title="Download Yearly Summary CSV">
                  📊 Yearly Summary CSV
                </button>
                <button onClick={exportYearlyDetailedCSV} className="export-btn detailed" title="Download Detailed CSV with Monthly Breakdown">
                  📋 Detailed CSV (monthly breakdown)
                </button>
                <button onClick={exportYearlyPDF} className="export-btn pdf" title="Download Full Yearly PDF Report">
                  📄 Full Yearly PDF
                </button>
              </div>

              {yearlyLoading ? (
                <div className="loading-state">
                  <div className="loading-spinner-large"></div>
                  <p>Loading yearly report...</p>
                </div>
              ) : yearlyReport ? (
                <div className="report-content">
                  <div className="report-summary">
                    <div className="summary-item">
                      <span className="summary-label">Year:</span>
                      <span className="summary-value">{yearlyReport.year}</span>
                    </div>
                    <div className="summary-item">
                      <span className="summary-label">Working Days:</span>
                      <span className="summary-value">{yearlyReport.totalWorkingDays}</span>
                    </div>
                    <div className="summary-item">
                      <span className="summary-label">Active Members:</span>
                      <span className="summary-value">{yearlyReport.activeStudents}</span>
                    </div>
                    {yearlyReport.isCurrentYear && (
                      <div className="summary-item">
                        <span className="summary-label current-month-badge">Current Year (up to today)</span>
                      </div>
                    )}
                  </div>

                  <div className="report-table-container">
                    <table className="report-table">
                      <thead>
                        <tr>
                          <th>#</th>
                          <th>Name</th>
                          <th>Lab ID</th>
                          <th>Type</th>
                          <th>Working Days</th>
                          <th>Present</th>
                          <th>Absent</th>
                          <th>Not Marked</th>
                          <th>Attendance %</th>
                        </tr>
                      </thead>
                      <tbody>
                        {yearlyReport.report.map((student, index) => (
                          <tr key={index}>
                            <td>{index + 1}</td>
                            <td className="name-cell">{student.name}</td>
                            <td>{student.labId}</td>
                            <td>{student.memberType || 'N/A'}</td>
                            <td>{student.workingDays}</td>
                            <td className="present-count">{student.totalPresent}</td>
                            <td className="absent-count">{student.totalAbsent}</td>
                            <td className="not-marked-count">{student.notMarked}</td>
                            <td>
                              <div className="percentage-cell">
                                <span className={`percentage-badge ${
                                  student.attendancePercentage >= 75 ? 'good' :
                                  student.attendancePercentage >= 50 ? 'warning' : 'poor'
                                }`}>
                                  {student.attendancePercentage.toFixed(1)}%
                                </span>
                              </div>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>

                  {yearlyReport.report.length > 0 && (
                    <div className="report-footer-stats">
                      <div className="footer-stat">
                        <span className="footer-stat-value">
                          {(yearlyReport.report.reduce((sum, s) => sum + s.attendancePercentage, 0) / yearlyReport.report.length).toFixed(1)}%
                        </span>
                        <span className="footer-stat-label">Average Yearly Attendance</span>
                      </div>
                      <div className="footer-stat">
                        <span className="footer-stat-value">
                          {yearlyReport.report.filter(s => s.attendancePercentage >= 75).length}
                        </span>
                        <span className="footer-stat-label">Good Attendance (≥75%)</span>
                      </div>
                      <div className="footer-stat">
                        <span className="footer-stat-value">
                          {yearlyReport.report.filter(s => s.attendancePercentage < 50).length}
                        </span>
                        <span className="footer-stat-label">Needs Attention (&lt;50%)</span>
                      </div>
                    </div>
                  )}
                </div>
              ) : (
                <div className="no-data">Select a year to view the report</div>
              )}
            </div>
          )}
        </div>

        {/* Member Detail Modal */}
        {showMemberDetail && (
          <div className="modal-overlay" onClick={() => setShowMemberDetail(null)}>
            <div className="modal-content member-detail-modal" onClick={e => e.stopPropagation()}>
              <div className="modal-header">
                <div>
                  <h3>{showMemberDetail.name}</h3>
                  <p className="modal-subtitle">{showMemberDetail.labId} | {showMemberDetail.memberType} | {showMemberDetail.email}</p>
                </div>
                <button className="modal-close" onClick={() => setShowMemberDetail(null)}>&times;</button>
              </div>

              <div className="modal-body">
                <div className="member-stats-summary">
                  <div className="stat-item">
                    <span className="stat-value">{showMemberDetail.workingDays}</span>
                    <span className="stat-label">Working Days</span>
                  </div>
                  <div className="stat-item present">
                    <span className="stat-value">{showMemberDetail.totalPresent}</span>
                    <span className="stat-label">Present</span>
                  </div>
                  <div className="stat-item absent">
                    <span className="stat-value">{showMemberDetail.totalAbsent}</span>
                    <span className="stat-label">Absent</span>
                  </div>
                  <div className="stat-item">
                    <span className="stat-value">{showMemberDetail.attendancePercentage.toFixed(1)}%</span>
                    <span className="stat-label">Attendance</span>
                  </div>
                </div>

                <h4>Attendance Log with Check-in Times</h4>

                {showMemberDetail.attendanceDetails && showMemberDetail.attendanceDetails.length > 0 ? (
                  <div className="detail-table-container">
                    <table className="detail-table">
                      <thead>
                        <tr>
                          <th>Date</th>
                          <th>Day</th>
                          <th>Status</th>
                          <th>Check-in Time</th>
                          <th>Remarks</th>
                        </tr>
                      </thead>
                      <tbody>
                        {showMemberDetail.attendanceDetails.map((record, idx) => {
                          const recordDate = new Date(record.date);
                          return (
                            <tr key={idx} className={record.status}>
                              <td>{recordDate.toLocaleDateString()}</td>
                              <td>{recordDate.toLocaleDateString('en-US', { weekday: 'short' })}</td>
                              <td>
                                <span className={`status-badge ${record.status}`}>
                                  {record.status === 'present' ? 'Present' : 'Absent'}
                                </span>
                              </td>
                              <td className="checkin-time-cell">
                                {record.checkInTime ? (
                                  <strong>{formatTime(record.checkInTime)}</strong>
                                ) : (
                                  <span className="no-time">-</span>
                                )}
                              </td>
                              <td>{record.remarks || '-'}</td>
                            </tr>
                          );
                        })}
                      </tbody>
                    </table>
                  </div>
                ) : (
                  <p className="no-records-message">No attendance records for this period.</p>
                )}
              </div>

              <div className="modal-footer">
                <button className="btn btn-secondary" onClick={() => setShowMemberDetail(null)}>
                  Close
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default AdminDashboard;
